import React, { useState } from "react";
import {
  Form,
  Field,
  Label,
  Input,
  FormGroup,
  Required,
  SubmitButton,
  BackButton,
  StyledPriceContainer,
  StyledPrice
} from "./PurchaseForm.styles";
import { FiMail, FiUser, FiPhone, FiMapPin, FiEdit  } from "react-icons/fi";
import {  cutOptions, suportOptions, useOptions,TEXT_PRICE, IMAGE_PRICE, SIZE_PRICE_PER_CM, calculateTotalPrice, CANVAS_WIDTH, CANVAS_HEIGHT } from '../../constants/options';
import { getBase64FromImageUrl, getBase64FromSVG, formatCanvasData, generateSVGfromTexts } from "../../utils/canvasUtils"; 

const PurchaseForm = ({
  // Props que vienen desde Canvas.jsx
  stageRef,
  contentType,
  texts,
  backgroundSrc,
  uploadedImages,
  description,
  size,
  cut,
  support,
  use,
  onBack,
  setSelectedTextId,
  setSelectedImageId

}) => {
  // Estado del propio formulario
  const [formData, setFormData] = useState({
    nombre: "",
    email: "",
    telefono: "",
    direccion: "",
    mensaje: "",
  });

  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // Maneja cambios de inputs
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const totalPrice = calculateTotalPrice(
    cut,
    support,
    use,
    texts.length,
    size.width,
    size.height,
    contentType,
    uploadedImages
  );

  const cutPrice = cutOptions.find(option => option.value === cut)?.price || 0;
  const supportPrice = suportOptions.find(option => option.value === support)?.price || 0;
  const usePrice = useOptions.find(option => option.value === use)?.price || 0;
  const textPrice = contentType === 'text' ? texts.length * TEXT_PRICE : 0;
  const imagePrice = contentType === 'image' && uploadedImages.length > 0 ? IMAGE_PRICE : 0;
  const sizePrice =
    (parseInt(size.width, 10) || 0) * SIZE_PRICE_PER_CM +
    (parseInt(size.height, 10) || 0) * SIZE_PRICE_PER_CM;

  const priceSummary = `
      Detalle del Precio:
      - Recorte: ${cutPrice} €
      - Soporte: ${supportPrice} €
      - Uso: ${usePrice} €
      ${
        contentType === 'text'
          ? `- Textos (${texts.length}): ${textPrice} €`
          : ''
      }
      ${
        contentType === 'image' && uploadedImages.length > 0
          ? `- Imagen: ${imagePrice} €`
          : ''
      }
      - Tamaño (Ancho: ${size.width}cm, Alto: ${size.height}cm): ${sizePrice} €
  `;


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");
    setLoading(true);
    setSelectedTextId(null);
    setSelectedImageId(null);

    await new Promise((resolve) => requestAnimationFrame(resolve));

    let dataURL = "";
    if (stageRef?.current) {
      try {
        dataURL = stageRef.current.toDataURL();
      } catch (err) {
        console.error("Error al obtener toDataURL() del stage:", err);
      }
    }

    const canvasData = {
      contentType,
      texts,
      uploadedImages,
      description,
      size,
      cut,
      support,
      use,
      canvasImage: dataURL,       
      backgroundImage: backgroundSrc,
      totalPrice,
      priceSummary
    };

    const formattedCanvasData = formatCanvasData(canvasData);
    const svgString = generateSVGfromTexts(CANVAS_WIDTH, CANVAS_HEIGHT, texts);
    const svgBase64 = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
    let pngData = "";
    if (stageRef?.current) {
      try {
        pngData = stageRef.current.toDataURL({ mimeType: 'image/png', pixelRatio: 2 });
      } catch (err) {
        console.error("Error al obtener PNG del stage:", err);
      }
    }

    let payload = {
      ...formData,
      canvasData: formattedCanvasData.text,     
      canvasImage: formattedCanvasData.image,   
      backgroundImage: formattedCanvasData.backgroundImage,
      uploadedImage: formattedCanvasData.uploadedImage,
      pngImage: pngData,
      textSVG: svgBase64,
      files: [
        { name: "SVG_canva.svg", content: svgBase64 },
        { name: "ImagenDeReferencia.png", content: pngData }
      ]
    };

    if (backgroundSrc && !backgroundSrc.startsWith("data:image")) {
      try {
        const base64Background = await getBase64FromImageUrl(backgroundSrc);
        payload.backgroundImage = base64Background;
      } catch (error) {
        console.error("Error al convertir el fondo a base64:", error);
      }
    }

    if (
      formattedCanvasData.uploadedImage &&
      formattedCanvasData.uploadedImage.endsWith(".svg")
    ) {
      try {
        const base64SVG = await getBase64FromSVG(formattedCanvasData.uploadedImage);
        payload.uploadedImage = base64SVG;
      } catch (error) {
        console.error("Error al convertir el SVG a base64:", error);
      }
    }

    try {
      const response = await fetch("https://www.lovemybar.es/wp-json/neones/v1/send-email", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSuccessMessage("¡Formulario enviado con éxito!");
        setFormData({
          nombre: "",
          email: "",
          telefono: "",
          direccion: "",
          mensaje: "",
        });
      } else {
        setErrorMessage("Hubo un error al enviar el formulario. Intenta de nuevo.");
      }
    } catch (error) {
      setErrorMessage("Error de conexión. Verifica tu red e intenta de nuevo.");
    }

    setLoading(false);
  };


  return (
    <Form onSubmit={handleSubmit}>
      {successMessage && <p style={{ color: "green" }}>{successMessage}</p>}
      {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

      <Field>
        <Label>
          <FiUser /> Nombre <Required>*</Required>
        </Label>
        <Input
          type="text"
          name="nombre"
          value={formData.nombre}
          onChange={handleChange}
          required
        />
      </Field>

      <FormGroup>
        <Field>
          <Label>
            <FiMail /> Correo electrónico <Required>*</Required>
          </Label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </Field>
        <Field>
          <Label>
            <FiPhone /> Teléfono <Required>*</Required>
          </Label>
          <Input
            type="tel"
            name="telefono"
            value={formData.telefono}
            onChange={handleChange}
            required
          />
        </Field>
      </FormGroup>

      <Field>
        <Label>
          <FiMapPin /> Dirección <Required>*</Required>
        </Label>
        <Input
          type="text"
          name="direccion"
          value={formData.direccion}
          onChange={handleChange}
          required
        />
      </Field>

      <Field>
        <Label>
          <FiEdit /> Descripción de tu Neón <Required>*</Required>
        </Label>
        <textarea
          name="mensaje"
          value={formData.mensaje}
          onChange={handleChange}
          required
          style={{
            width: "100%",
            height: "80px",
            padding: "10px",
            borderRadius: "5px",
            border: "1px solid #ccc"
          }}
        />
      </Field>

      {/* Mostrar precio total si el contenido es texto */}
      {contentType === 'text' && (
        <Field>
          <StyledPriceContainer>
            <span>Precio Aproximado:</span>
            <StyledPrice>{totalPrice} €</StyledPrice>
          </StyledPriceContainer>
        </Field>
      )}

      <SubmitButton type="submit" disabled={loading}>
        {loading ? "Enviando..." : "ENVIAR"}
      </SubmitButton>
      <BackButton
        type="button"
        onClick={() =>
          onBack({
            contentType,
            texts,
            backgroundSrc,
            uploadedImages,
            description,
            size,
            cut,
            support,
            use
          })
        }
      >
        Volver al Canvas
      </BackButton>
    </Form>
  );
};

export default PurchaseForm;